import { useCallback, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '../lib/api'
import { KIND_LABEL, type Deck, type DeckSlideSpec, type RunSummary, type SlideSpec } from '../lib/types'
import { Brand, ThemeToggle } from '../components/Bits'
import { SlideForm } from '../components/deck/SlideForm'
import { useAuth } from '../lib/auth'
import { useSeo } from '../lib/seo'
import { when } from '../lib/plural'

/** Автосохранение не чаще, чем раз в столько миллисекунд после правки. */
const AUTOSAVE_DEBOUNCE_MS = 800

type SaveStatus = 'saved' | 'saving' | 'error' | 'conflict'

const STATUS_LABEL: Record<SaveStatus, string> = {
  saved: 'Сохранено',
  saving: 'Сохраняю…',
  error: 'Ошибка сохранения',
  conflict: 'Демонстрацию правили в другом окне',
}

const newSlideId = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID().slice(0, 8)
    : `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`

/** Форма отдаёт SlideSpec; демонстрация хранит его же плюс стабильный id. */
function toDeckSlide(spec: SlideSpec, id: string): DeckSlideSpec {
  return {
    id,
    kind: spec.kind,
    title: spec.title,
    options: spec.options ?? [],
    timerSeconds: spec.timerSeconds ?? null,
    maxTextLength: spec.maxTextLength ?? 140,
    resultView: spec.resultView ?? null,
    emojiSet: spec.emojiSet ?? null,
    moderated: spec.moderated ?? false,
  }
}

/**
 * Редактор демонстрации — черновик без эфира. Сохранение автоматическое: человек
 * закрывает вкладку, не думая про кнопки, и демонстрация обязана уехать на сервер
 * целиком. Конфликт (правили в двух окнах) не перезатирается молча — второе
 * окно узнаёт, что отстало, и предлагает перезагрузить.
 */
export default function DeckEditor() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { user, ready } = useAuth()
  const [deck, setDeck] = useState<Deck | null>(null)
  const [title, setTitle] = useState('')
  const [slides, setSlides] = useState<DeckSlideSpec[]>([])
  const [editing, setEditing] = useState<DeckSlideSpec | null>(null)
  const [status, setStatus] = useState<SaveStatus>('saved')
  const [missing, setMissing] = useState(false)
  const [runs, setRuns] = useState<RunSummary[] | null>(null)
  const [launching, setLaunching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [maxSlides, setMaxSlides] = useState<number | null>(null)
  const dirty = useRef(false)

  useSeo({ title: `${title || 'Демонстрация'} · редактор — clapme`, noindex: true })

  const load = useCallback(() => {
    api.decks
      .get(id)
      .then((d) => {
        setDeck(d)
        setTitle(d.title)
        setSlides(d.slides)
        dirty.current = false
        setStatus('saved')
      })
      .catch(() => setMissing(true))
    api.decks.runs(id).then(setRuns).catch(() => setRuns([]))
  }, [id])

  useEffect(() => {
    if (ready && user) load()
  }, [ready, user, load])

  // Лимит слайдов своего тарифа — для счётчика над списком.
  useEffect(() => {
    if (user) api.billing.me().then((me) => setMaxSlides(me.plan.limits.maxSlides)).catch(() => {})
  }, [user])

  const save = useCallback(async () => {
    if (!deck) return
    setStatus('saving')
    try {
      const saved = await api.decks.save(id, { title, slides, revision: deck.revision })
      setDeck((current) => (current ? { ...current, revision: saved.revision } : current))
      dirty.current = false
      setStatus('saved')
    } catch (e) {
      if (e instanceof ApiError && e.code === 'plan_limit') {
        // Тарифная ошибка не «переживём молча»: человек должен узнать, почему
        // сохранение не проходит, и увидеть выход — а не generic-повтор.
        setError(e.message)
      }
      setStatus(e instanceof ApiError && e.code === 'deck_conflict' ? 'conflict' : 'error')
    }
  }, [deck, id, title, slides])

  // Правки помечают состояние грязным через touch(); сам эффект реагирует на
  // смену title/slides — то есть ровно на те состояния, которые уезжают в PUT.
  useEffect(() => {
    if (!deck || !dirty.current) return
    const timer = window.setTimeout(() => void save(), AUTOSAVE_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
  }, [title, slides, deck, save])

  const touch = () => {
    dirty.current = true
  }

  /**
   * Запуск копирует демонстрацию из базы — несохранённые правки в эфир не уехали бы.
   * Поэтому сначала дозавершаем сохранение, потом стартуем; пульт получает
   * host-cookie прямо в ответе и открывается сразу.
   */
  const launch = async () => {
    setLaunching(true)
    setError(null)
    try {
      if (dirty.current || status === 'saving') await save()
      const run = await api.decks.run(id)
      navigate(`/admin/${run.code}`)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось запустить')
    } finally {
      setLaunching(false)
    }
  }

  const submitSlide = (spec: SlideSpec) => {
    if (editing) {
      setSlides((current) => current.map((s) => (s.id === editing.id ? toDeckSlide(spec, s.id) : s)))
      setEditing(null)
    } else {
      setSlides((current) => [...current, toDeckSlide(spec, newSlideId())])
    }
    touch()
  }

  const removeSlide = (slideId: string) => {
    setSlides((current) => current.filter((s) => s.id !== slideId))
    touch()
  }

  if (!ready || (user && !deck && !missing)) return null

  if (!user) {
    return (
      <div className="wrap col" style={{ gap: 20 }}>
        <header className="row spread">
          <Brand />
          <ThemeToggle />
        </header>
        <div className="card col">
          <h2 style={{ margin: 0 }}>Редактор доступен после входа</h2>
          <p className="muted" style={{ margin: 0 }}>
            Демонстрации принадлежат аккаунту — войдите, чтобы продолжить работу.
          </p>
        </div>
      </div>
    )
  }

  if (missing) {
    return (
      <div className="wrap col" style={{ gap: 20 }}>
        <header className="row spread">
          <Brand />
          <ThemeToggle />
        </header>
        <div className="card col">
          <h2 style={{ margin: 0 }}>Демонстрация не найдена</h2>
          <p className="muted" style={{ margin: 0 }}>
            Такой демонстрации нет — её удалили или она принадлежит другому аккаунту.
          </p>
          <Link to="/demos">К списку демонстраций</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="wrap col" style={{ gap: 20, maxWidth: 1180 }}>
      <header className="row spread row-wrap">
        <div className="row row-wrap" style={{ gap: 14 }}>
          <Brand />
          <input
            aria-label="Название демонстрации"
            value={title}
            maxLength={120}
            onChange={(e) => {
              setTitle(e.target.value)
              touch()
            }}
            style={{ fontWeight: 700 }}
          />
          <span className="muted tiny" data-testid="save-status">
            {STATUS_LABEL[status]}
          </span>
        </div>
        <div className="row">
          <Link className="account-login" to="/demos">
            Все демонстрации
          </Link>
          <ThemeToggle />
        </div>
      </header>

      {status === 'conflict' && (
        <div className="card row spread row-wrap">
          <div className="col" style={{ gap: 2 }}>
            <strong>Демонстрацию правили в другом окне</strong>
            <span className="muted tiny">
              Ваша копия отстала, и сохранение заблокировано — иначе оно затёрло бы чужие правки.
            </span>
          </div>
          <button className="btn btn-primary" onClick={load}>
            Загрузить свежую версию
          </button>
        </div>
      )}
      {status === 'error' && (
        <div className="toast" role="status">
          {error ?? 'Не получилось сохранить — проверим ещё раз при следующей правке'}
        </div>
      )}
      <div className="admin-grid">
        <div className="col">
          <SlideForm
            key={editing?.id ?? 'new'}
            initial={editing}
            onSubmit={submitSlide}
            onCancel={editing ? () => setEditing(null) : undefined}
          />

          <div className="card col">
            <div className="row spread">
              <h3 className="card-title">Слайды</h3>
              <span className="muted tiny">
                {slides.length}
                {maxSlides != null && ` из ${maxSlides}`}
              </span>
            </div>
            <div className="slide-list">
              {slides.length === 0 && (
                <p className="muted tiny" style={{ margin: 0 }}>
                  Пока ни одного — добавьте первый сверху
                </p>
              )}
              {slides.map((slide, index) => (
                <div className="slide-chip" key={slide.id}>
                  <span className="slide-chip-num">{index + 1}</span>
                  <button
                    className="link-button grow"
                    style={{ border: 0 }}
                    onClick={() => setEditing(slide)}
                  >
                    <span>{slide.title}</span>
                    <br />
                    <span className="muted tiny">
                      {KIND_LABEL[slide.kind]}
                      {slide.options.some((o) => o.correct) && ' · с баллами'}
                    </span>
                  </button>
                  <button
                    className="btn btn-ghost tiny"
                    title="Править слайд"
                    aria-label={`Править слайд ${index + 1}`}
                    style={{ padding: '6px 9px' }}
                    onClick={() => setEditing(slide)}
                  >
                    ✎
                  </button>
                  <button
                    className="btn btn-ghost tiny"
                    title="Удалить слайд"
                    aria-label={`Удалить слайд ${index + 1}`}
                    style={{ padding: '6px 9px' }}
                    onClick={() => removeSlide(slide.id)}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="col">
          <div className="card col" style={{ gap: 14 }}>
            <h2 style={{ margin: 0 }}>Готовы к эфиру?</h2>
            <p className="muted" style={{ margin: 0 }}>
              Запуск копирует слайды в живой эфир — зал заходит по коду, как обычно. Правки прямо
              в эфире демонстрацию не меняют, а пульт открывается с любого устройства, где вы вошли.
            </p>
            <button className="btn btn-primary btn-big" onClick={() => void launch()} disabled={launching}>
              {launching ? 'Запускаем…' : 'Запустить эфир'}
            </button>
            {error && <span className="muted tiny">{error}</span>}
          </div>

          <div className="card col" style={{ gap: 10 }}>
            <h3 className="card-title" style={{ margin: 0 }}>
              Запуски
            </h3>
            {runs === null && <span className="muted tiny">Загружаем…</span>}
            {runs?.length === 0 && <span className="muted tiny">Эту демонстрацию ещё не запускали</span>}
            {runs?.map((run) => (
              <div className="row spread" key={run.code} style={{ gap: 10 }}>
                <Link
                  to={`/results/${run.code}`}
                  className="grow"
                  style={{ textDecoration: 'none', color: 'inherit' }}
                >
                  {when(run.startedAt)} · {run.participants ?? '—'} в зале
                </Link>
                {run.live && <span className="badge badge-live">идёт</span>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
